import DispatchTransferWorkflow from '../components/inventory/DispatchTransferWorkflow';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDeliveryGuideById, getProducts, getDeliverySummaryForPurchase } from '../api';
import { jsPDF, autoTable, loadImageAsDataUrl } from '../utils/pdfUtils';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';
import { normalizePaginatedResponse } from '../utils/pagination';
import { notify } from '../utils/alertBus';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';
import {
  buildDeliveryGuideLineKey,
  buildDeliveryGuideLineKeys,
  resolveVarietyPriceForGuideItem,
} from '../utils/deliveryGuideItems';

const DeliveryGuideDetailsPage = () => {
  const { branchId, purchaseId, guideId } = useParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const resolvedBranchId = branchId || localStorage.getItem('selectedBranchId');
  const { user } = useCurrentUser();
  const canReadGuides = hasPermission(user, 'delivery_guide.read');
  const canReadProducts = hasPermission(user, 'product.read');

  const refreshProducts = useCallback(async () => {
    if (!resolvedBranchId || !canReadProducts) {
      if (!canReadProducts) {
        setProducts([]);
      }
      return;
    }
    try {
      const productsData = await getProducts(resolvedBranchId, { pageSize: 0 });
      const normalizedProducts = normalizePaginatedResponse(productsData);
      setProducts(normalizedProducts.data || []);
    } catch (err) {
      console.error('Error refreshing products:', err);
    }
  }, [resolvedBranchId, canReadProducts]);

  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) {
      refreshProducts();
      return;
    }
    if (nextProducts === products) return;
    setProducts(nextProducts);
  }, [products, refreshProducts]);

  useProductEvents(resolvedBranchId, handleProductEvent);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(null); setGuide(null);
    const fetchData = async () => {
      try {
        const [guideData, productsData] = await Promise.all([
          getDeliveryGuideById(resolvedBranchId, guideId),
          canReadProducts ? getProducts(resolvedBranchId, { pageSize: 0 }) : Promise.resolve([]),
        ]);
        if (!active) return;
        setGuide(guideData);
        const normalizedProducts = normalizePaginatedResponse(productsData);
        setProducts(normalizedProducts.data || []);
        const resolvedPurchaseId = purchaseId || guideData?.purchaseId;
        if (resolvedPurchaseId && canReadGuides) {
          const summaryData = await getDeliverySummaryForPurchase(
            resolvedBranchId,
            resolvedPurchaseId
          );
          if (active) setSummary(summaryData || null);
        } else {
          setSummary(null);
        }
      } catch (err) {
        console.error('Error fetching delivery guide details:', err);
        if (active) setError('Error al cargar la guía de despacho.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => { active = false; };
  }, [resolvedBranchId, purchaseId, guideId, canReadGuides, canReadProducts]);

  const itemsWithRefs = useMemo(() => {
    if (!guide) return [];
    return (guide.items || []).map((item) => {
      const product = products.find((p) =>
        p.Varieties?.some((v) => v.id === item.varietyId)
      );
      const variety = product?.Varieties?.find((v) => v.id === item.varietyId);
      const priceObj = resolveVarietyPriceForGuideItem(variety, item);
      const imageSrc =
        priceObj?.priceImage ||
        variety?.varietyImage ||
        product?.productImage ||
        'https://placehold.co/60';
      const quantityInGuide = item.deliveredQuantity ?? item.quantity ?? 0;

      return {
        ...item,
        productName: item.productName || product?.productName || item.varietyName,
        imageSrc,
        quantityInGuide,
      };
    });
  }, [guide, products]);

  const guideItemsByKey = useMemo(() => {
    const map = {};
    (guide?.items || []).forEach((item) => {
      buildDeliveryGuideLineKeys(item).forEach((key) => {
        if (key) {
          map[key] = item;
        }
      });
    });
    return map;
  }, [guide]);

  const handleDownloadPdf = async () => {
    if (!guide || !canReadGuides) return;
    try {
      const isTransferGuide = guide?.guideType === 'transfer';
      const tableElement = document.getElementById('delivery-guide-table');
      if (tableElement) {
        const imgElements = Array.from(tableElement.querySelectorAll('tbody img'));
        await Promise.all(
          imgElements.map(async (img) => {
            if (img.getAttribute('data-pdf-src')) return;
            const dataUrl = await loadImageAsDataUrl(img.src);
            if (dataUrl) {
              img.setAttribute('data-pdf-src', dataUrl);
              const format = dataUrl.includes('image/png') ? 'PNG' : 'JPEG';
              img.setAttribute('data-pdf-format', format);
            }
          })
        );
      }

      const doc = new jsPDF('p', 'pt', 'a4');
      const marginX = 40;
      let cursorY = 48;

      const pageWidth = doc.internal.pageSize.getWidth();
      const title = isTransferGuide ? 'Guía de Despacho (Traslado)' : 'Guía de Despacho';
      doc.setFontSize(16);
      const titleWidth = doc.getTextWidth(title);
      const titleX = (pageWidth - titleWidth) / 2;
      doc.text(title, titleX, cursorY);
      cursorY += 24;

      if (!isTransferGuide && guide.Purchase && guide.Purchase.Client) {
        const client = guide.Purchase.Client;
        doc.setFontSize(11);
        doc.text(`Cliente: ${client.name}`, marginX, cursorY);
        cursorY += 16;
        if (client.rut) {
          doc.text(`RUT: ${client.rut}`, marginX, cursorY);
          cursorY += 14;
        }
        if (client.address || client.city) {
          doc.text(
            `Dirección: ${client.address || ''}${client.city ? `, ${client.city}` : ''}`,
            marginX,
            cursorY
          );
          cursorY += 14;
        }
        cursorY += 6;
      }

      doc.setFontSize(10);
      if (isTransferGuide) {
        if (guide.SourceBranch?.branchName || guide.sourceBranchId) {
          doc.text(
            `Sucursal origen: ${guide.SourceBranch?.branchName || guide.sourceBranchId}`,
            marginX,
            cursorY
          );
          cursorY += 14;
        }
        if (guide.DestinationBranch?.branchName || guide.destinationBranchId) {
          doc.text(
            `Sucursal destino: ${guide.DestinationBranch?.branchName || guide.destinationBranchId}`,
            marginX,
            cursorY
          );
          cursorY += 14;
        }
        if (guide.stockListId) {
          doc.text(`Stock list destino: ${guide.stockListId}`, marginX, cursorY);
          cursorY += 14;
        }
      } else {
        doc.text(`Compra: ${guide.purchaseId}`, marginX, cursorY);
        cursorY += 14;
      }
      if (guide.guideNumber) {
        doc.text(`Folio guía: ${guide.guideNumber}`, marginX, cursorY);
        cursorY += 14;
      }
      if (guide.driverName) {
        doc.text(`Chofer: ${guide.driverName}`, marginX, cursorY);
        cursorY += 14;
      }
      if (guide.vehiclePlate) {
        doc.text(`Patente: ${guide.vehiclePlate}`, marginX, cursorY);
        cursorY += 14;
      }
      if (guide.deliveryDate) {
        const dateStr = new Date(guide.deliveryDate).toLocaleDateString();
        doc.text(`${guide.dispatchOperationId ? 'Fecha de salida' : 'Fecha de entrega'}: ${dateStr}`, marginX, cursorY);
        cursorY += 18;
      } else {
        cursorY += 10;
      }

      autoTable(doc, {
        startY: cursorY,
        margin: { left: marginX, right: marginX },
        html: '#delivery-guide-table',
        styles: { fontSize: 10, cellPadding: 6, valign: 'middle' },
        didDrawCell: (data) => {
          if (data.cell.section === 'body' && data.column.index === 0) {
            const td = data.cell.raw;
            const img = td?.getElementsByTagName('img')[0];
            if (!img) return;

            const src = img.getAttribute('data-pdf-src') || img.src;
            if (!src || !src.startsWith('data:image')) return;

            const cell = data.cell;
            const dim = cell.height - cell.padding('vertical');
            const x = cell.x + cell.padding('left');
            const y = cell.y + cell.padding('top');

            const formatAttr = img.getAttribute('data-pdf-format');
            const format =
              formatAttr || (src.includes('image/png') ? 'PNG' : 'JPEG');

            try {
              doc.addImage(src, format, x, y, dim, dim);
            } catch {
            }
          }
        },
      });

      const baseName = guide.Purchase?.Client?.name?.replace(/\s+/g, '_') || 'guia';
      const suffix = guide?.guideType === 'transfer' ? 'traslado' : 'venta';
      const resolvedPurchaseId = purchaseId || guide.purchaseId || 'sin_compra';
      const fileName = `guia_despacho_${suffix}_${baseName}_${resolvedPurchaseId}_${guideId}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('Error generating delivery guide PDF:', err);
      notify('No se pudo generar el PDF de la guía.');
    }
  };

  if (loading) {
    return (
      <p className="p-4 text-center">
        <InlineLoading label="Cargando guía de despacho" />
      </p>
    );
  }

  if (error) {
    return <p className="p-4 text-center text-red-600">{error}</p>;
  }

  if (!guide) {
    return (
      <p className="p-4 text-center text-red-600">
        No se encontró la guía de despacho.
      </p>
    );
  }

  const isTransferGuide = guide?.guideType === 'transfer';
  const resolvedPurchaseId = purchaseId || guide?.purchaseId;

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-3">
          <button
            onClick={() =>
              resolvedPurchaseId
                ? navigate(`/branches/${resolvedBranchId}/purchases/${resolvedPurchaseId}`)
                : navigate(-1)
            }
            className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
          >
            Volver
          </button>
          {canReadGuides && (
            <button
              onClick={handleDownloadPdf}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
            >
              Descargar PDF
            </button>
          )}
        </div>

        {guide.dispatchOperationId && <DispatchTransferWorkflow key={`${user?.selectedCompanyId}:${resolvedBranchId}:${guide.id}`}
          branchId={resolvedBranchId} guideId={guide.id} user={user}
          onSaved={fresh => setGuide(current => ({ ...current, ...fresh }))} />}
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold mb-4 text-center">
            {isTransferGuide ? 'Guía de Despacho (Traslado)' : 'Guía de Despacho'}
          </h1>

          {!isTransferGuide && guide.Purchase && guide.Purchase.Client && (
            <div className="mb-4 text-sm text-gray-700">
              <h2 className="text-lg font-semibold mb-1">Cliente</h2>
              <p className="font-semibold">{guide.Purchase.Client.name}</p>
              {guide.Purchase.Client.rut && (
                <p>RUT: {guide.Purchase.Client.rut}</p>
              )}
              {(guide.Purchase.Client.address || guide.Purchase.Client.city) && (
                <p>
                  {guide.Purchase.Client.address}
                  {guide.Purchase.Client.city &&
                    `, ${guide.Purchase.Client.city}`}
                </p>
              )}
            </div>
          )}

          <div className="mb-4 text-sm text-gray-700">
            {isTransferGuide ? (
              <>
                <p>
                  Sucursal origen:{' '}
                  {guide.SourceBranch?.branchName || guide.sourceBranchId || '-'}
                </p>
                <p>
                  Sucursal destino:{' '}
                  {guide.DestinationBranch?.branchName || guide.destinationBranchId || '-'}
                </p>
                {guide.stockListId && <p>Stock list destino: {guide.stockListId}</p>}
              </>
            ) : (
              <p>Compra: {guide.purchaseId}</p>
            )}
            {guide.guideNumber && <p>Folio guía: {guide.guideNumber}</p>}
            {guide.driverName && <p>Chofer: {guide.driverName}</p>}
            {guide.vehiclePlate && <p>Patente: {guide.vehiclePlate}</p>}
            {guide.deliveryDate && (
              <p>
                {guide.dispatchOperationId ? 'Fecha de salida' : 'Fecha de entrega'}:{' '}
                {new Date(guide.deliveryDate).toLocaleDateString()}
              </p>
            )}
            {guide.notes && (
              <p className="mt-2">
                <span className="font-semibold">Notas:</span> {guide.notes}
              </p>
            )}
          </div>

          {summary && !isTransferGuide && (
            <div className="mb-4 text-sm text-gray-700">
              <h2 className="text-base font-semibold mb-1">
                Resumen de esta compra
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-1 text-left font-medium text-gray-600">
                        Producto
                      </th>
                      <th className="px-2 py-1 text-left font-medium text-gray-600">
                        Unidad
                      </th>
                      <th className="px-2 py-1 text-right font-medium text-gray-600">
                        Pedido
                      </th>
                      <th className="px-2 py-1 text-right font-medium text-gray-600">
                        Entregado total
                      </th>
                      <th className="px-2 py-1 text-right font-medium text-gray-600">
                        En esta guía
                      </th>
                      <th className="px-2 py-1 text-right font-medium text-gray-600">
                        Restante
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summary.summary || []).map((line, idx) => {
                      const key = buildDeliveryGuideLineKey(line) || `${line.varietyName}-${idx}`;
                      const inThisGuide = buildDeliveryGuideLineKeys(line).reduce(
                        (acc, lineKey) => {
                          if (acc !== null) return acc;
                          return guideItemsByKey[lineKey]?.deliveredQuantity ?? null;
                        },
                        null
                      ) ?? 0;

                      return (
                        <tr key={key} className="border-t">
                          <td className="px-2 py-1">{line.varietyName}</td>
                          <td className="px-2 py-1">
                            {line.metricUnit || 'unidad'}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {line.orderedQuantity}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {line.deliveredSoFar}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {inThisGuide}
                          </td>
                          <td
                            className={`px-2 py-1 text-right ${
                              line.remainingQuantity > 0
                                ? 'text-orange-600 font-semibold'
                                : 'text-green-600 font-semibold'
                            }`}
                          >
                            {line.remainingQuantity}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table
              id="delivery-guide-table"
              className="min-w-full divide-y divide-gray-200 text-sm"
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Imagen
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variedad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unidad
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {isTransferGuide ? 'Cantidad trasladada' : 'Cantidad en Guía'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {itemsWithRefs.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3">
                      <img
                        src={row.imageSrc}
                        alt={row.varietyName}
                        className="w-12 h-12 object-cover rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      {row.productName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.varietyName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.metricUnit || 'unidad'}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                      {row.quantityInGuide}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryGuideDetailsPage;
